/**
 * Service for making the options from the project available to the application
 */

import { Injectable } from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {Observable} from "rxjs";
import {Donation} from "../donation/donation";
import {HttpClient} from "@angular/common/http";

declare let __twingle_type: any;
declare let __twingle_projectData: any;
declare let __twingle_transactionapi: any;
declare let __twingle_identifier: any;
declare let __twingle_qrcodeurl: any;
declare let __twingle_usercountryUrl: string;
declare let __twingle_transactiontokenurl: string;
declare let __twingle_lookuphost: string;
declare let __twingle_visitUrl: string;
declare let __twingle_mbe4MaxAmount: any;


@Injectable()
export class OptionService {

    /**
     * Holds the security token
     * @type {null}
     */
    private token: string = null;

    /**
     * toggle if the visit was already tracked or not
     * @type {boolean}
     */
    private visitTracked: boolean = false;

    /**
     * The constructor
     * @param translate
     * @param http
     */
    constructor(private translate: TranslateService, private http: HttpClient) {}

    /**
     * Returns a text from the translator but checks for some textkeys if there is one in the database for this
     * @param textkey
     * @param parameter
     * @returns {any}
     */
    public getText(textkey: string, parameter: any = null) {
        let option = null;

        // Mapping between textkey and option
        switch (textkey) {
            case "contact.newsletter.checkbox":
                option = "text_checkbox_newsletter";
                break;
            case "thankyou.text.banktransfer":
                option = "text_result_banktransfer";
                break;
            case "thankyou.text.mbe4":
                option = "text_result_mbe4";
                break;
            case "thankyou.text.betterpayment":
            case "thankyou.text.debit":
                option = "text_result_debit";
                break;
            case "thankyou.text.paypal":
                option = "text_result_paypal";
                break;
            case "thankyou.text.betterpayment_creditcard":
            case "thankyou.text.sixpay":
                option = "text_result_creditcard";
                break;
            case "thankyou.text.sofortueberweisung":
                option = "text_result_sofortueberweisung";
                break;
            case "thankyou.text.generic":
                option = "text_result_generic";
                break;
        }

        // did we have an alternative option?
        if(option != null) {
            // check if we have a text
            let text = this.getOption(option);
            if(text != null && text.length > 0) {
                // check if we have to replace some parameters in the text
                if(parameter != null) {
                    // replace all parameter
                    for(let parameterName in parameter) {
                        let search = new RegExp("\{\{" + parameterName + "\}\}");
                        text = text.replace(search, parameter[parameterName]);
                    }
                }

                // return the text
                return text;
            }
        }

        // if there was no option, return the text from the translate service
        return this.translate.instant(textkey, parameter);
    }

    /**
     * Returns the URL for counting a visit
     * @returns {string}
     */
    public getVisitUrl(): string {
        return __twingle_visitUrl;
    }

    /**
     * Returns the users Country
     * 
     * @returns {Observable}
     */
    public getUsersCountry(): Observable<any> {
        // getting the users country based on it's IP from the server. We are using POST here to bypass the varnish cache
        return this.http.post(__twingle_usercountryUrl, {});
    }

    /**
     * Returns the users Country
     *
     * @returns {Observable}
     */
    public getToken(): Observable<any> {
        // getting a token from the server for validating the donation. We are using POST here to bypass the varnish cache
        return this.http.post(__twingle_transactiontokenurl.replace('theidentifier', this.getIdentifier()), {});
    }

    /**
     * tracks a visit on the server of this donation
     * @param {Donation} donation
     */
    public trackVisit(donation: Donation): void {
        // track the visit only once
        if(this.visitTracked === true) {
            return;
        }
        this.visitTracked = true;

        this.http.post(
            this.getVisitUrl().replace('thetoken', donation.metaData.token), {})
            .subscribe(function(res) {
                    console.info("counted visit", res);
                }, error => {
                    console.warn('could not track visit, ignore this.', error);
                }
            );
    }

    /**
     * Returns the host of the transaction API
     * @returns {any}
     */
    public static getTransactionApiHost(): any {
        return __twingle_transactionapi;
    }

    /**
     * Returns the host of the transaction API
     * @returns {any}
     */
    public static getLookupHost(): any {
        return __twingle_lookuphost;
    }

    /**
     * Returns the URL for the QR Code
     * @returns {any}
     */
    public getQrCodeUrl(): any {
        return __twingle_qrcodeurl;
    }

    /**
     * Returns the host of the transaction API
     * @returns {any}
     */
    public getIdentifier(): any {
        return __twingle_identifier;
    }

    /**
     * Returns the project identifier static
     * @returns {string | null}
     */
    public static getIdentifierStatic(): string | null {
        return __twingle_identifier ? __twingle_identifier : null;
    }    

    /**
     * Returns the conatiner id of the twingleIframe
     */
    public static getConatinerId(): any {
        const matches = document.location.pathname.match(/\/(_?[a-zA-z0-9]+)$/);
        if (matches) {
            return matches[1];
        }

        return __twingle_type;
    }

    /**
     * Returns the maximum amount for mbe payment
     * @returns {any}
     */
    public getMbe4MaxAmount(): number {
        return __twingle_mbe4MaxAmount * 1;
    }

    /**
     * Returns the list of available payment methods
     * @returns {*}
     */
    public getPaymentMethods(): any {
        return __twingle_projectData['payment_methods'];
    }

    /**
     * Returns the list of available donation targets
     * @returns {*}
     */
    public getDonationTargets(): any {
        return __twingle_projectData['donation_targets'];
    }

    /**
     * overwrite the available donation targets with new values
     * @param targets
     */
    public overwriteDonationTargets(targets: any) {
        __twingle_projectData['donation_targets'] = targets;
    }

    /**
     * Returns the project_id
     * @returns {*}
     */
    public getProjectId(): any {
        return __twingle_projectData['project_id'];
    }

    /**
     * Returns the name of the project
     * @returns {*}
     */
    public getProjectName(): any {
        return __twingle_projectData['project_name'];
    }

    /**
     * Returns the project_id
     * @returns {*}
     */
    public getOrganisationId(): any {
        return __twingle_projectData['organisation_id'];
    }

    /**
     * Returns the projects target. Can be null if there is none
     * @returns {*}
     */
    public getProjectTarget(): number {
        return __twingle_projectData['project_target']? __twingle_projectData['project_target'] : null;
    }

    /**
     * Get the transaction type for this project.
     */
    public getTransactionType(): string {
      return __twingle_projectData['transaction_type'];
    }

    /**
     * Returns if the project has a target
     * @returns {*}
     */
    public hasProjectTarget(): boolean {
        return __twingle_projectData['project_target']? true : false;
    }

    /**
     * Returns the update url for the current project target
     * @returns {string}
     */
    public getProjectTargetUpdateUrl(): string {
        return __twingle_projectData['project_target_update_url']? __twingle_projectData['project_target_update_url'] : null;
    }

    /**
     * Returns the reached projects target percentage
     * @returns {*}
     */
    public getProjectTargetPercentage(): number {
        return __twingle_projectData['project_target_percentage']? __twingle_projectData['project_target_percentage'] : null;
    }

    /**
     * Returns the reached projects target percentage
     * @returns {*}
     */
    public getProjectTargetAmount(): number {
        return __twingle_projectData['project_target_amount'] ?? 0;
    }

    /**
     * Returns the reached projects target percentage
     * @returns {*}
     */
    public getProjectTargetDonators(): number {
        return __twingle_projectData['project_target_donators'] ?? 0;
    }

    /**
     * Returns the image directory
     * @returns {String}
     */
    public getImageDirectory(): string {
        return __twingle_projectData['image_directory']? __twingle_projectData['image_directory'] : null;
    }

    /**
     * Returns if it is allowed to donate more than the project target
     * @returns {*}
     */
    public getAllowMore(): boolean {
        return __twingle_projectData['allow_more'];
    }

    /**
     * Returns all donations for the project
     * @returns {Array}
     */
    public getProjectDonations(): any {
        return __twingle_projectData['project_donations']? __twingle_projectData['project_donations'] : [];
    }

    /**
     * Sets all donations for the project
     * @returns {Array}
     */
    public setProjectDonations(projectDonations): any {
        __twingle_projectData['project_donations'] = projectDonations;
    }

    /**
     * Returns an option value by its name. If the option does not exists, null is returned
     * @param option
     * @returns {null}
     */
    public getOption(option: string): any {
        return OptionService.getOptionStatic(option);
    }

    /**
     * Returns an event option value by its name. If the option does not exists, null is returned
     * @param option
     * @returns {null}
     */
    public getEventOption(option: string): any {
        return OptionService.getEventOptionStatic(option);
    }

    /**
     * Overwrites an option so that components can influence the settings
     * @param option
     * @param value
     * @returns {null}
     */
    public overwriteOption(option: string, value: any): void {
        OptionService.overwriteOptionStatic(option, value);
    }

    /**
     * Returns an option value by its name. If the option does not exists, null is returned
     * @param option
     * @returns {null}
     */
    public static getOptionStatic(option: string): any {
        return typeof __twingle_projectData['options'][option] != "undefined"? __twingle_projectData['options'][option] : null;
    }

    /**
     * Returns an event option value by its name. If the option does not exists, null is returned
     * @param option
     * @returns {null}
     */
    public static getEventOptionStatic(option: string): any {
        return typeof __twingle_projectData['event'][option] != "undefined"? __twingle_projectData['event'][option] : null;
    }

    /**
     * overwrites the value of an option
     * @param option
     * @param value
     */
    public static overwriteOptionStatic(option: string, value: any): void {
        __twingle_projectData['options'][option] = value;
    }

    /**
     * returns data from the Organisation of this project
     * @param key
     * @returns {null}
     */
    public getOrganisationData(key: string): any {
        return OptionService.getOrganisationDataStatic(key);
    }

    /**
     * returns data from the Organisation of this project
     * @param key
     * @returns {null}
     */
    public static getOrganisationDataStatic(key: string): any {
      return __twingle_projectData?.organisation?.hasOwnProperty(key) ? __twingle_projectData['organisation'][key] : null;
    }

    /**
     * Returns the type of the displayed widget (page, widget, form)
     * @returns {any}
     */
    public getType(): string {
        return __twingle_type;
    }

    /**
     * Returns the type of the project
     */
    public getProjectType(): string {
        return __twingle_projectData['type'];
    }

    /**
     * get the bonusdata
     */
    public getBonus(): any {
        return __twingle_projectData['bonus']?? [];
    }
}
